import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { SNSmartTeamTicket } from '@nsc/servicenow/types.js';
import { expectedError } from '@nsc/smartteams-shared/api/expected-error.js';
import { SmartTeamsFieldEngineer } from '@nsc/smartteams-shared/db/SmartTeamsFieldEngineer.js';
import { ColleagueEngineer, PudoLocation, PudoLocationCache } from '../db/PudoLocation.js';
import { ServiceNowTicket } from '../db/ServiceNowTicket.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';
import { getCurrentAppUser, snAssignedTo } from './_assignee-guard.js';
import { geocode, mapSNTicketForColleagueView } from './_colleague-mapping.js';
import { type ColleagueScope, colleagueScope, engineerTypeFilter } from './_colleague-scope.js';
import { attachWorkNotes } from './_ticket-notes.js';

/**
 * "Other FE tickets" — the colleague view.
 *
 * A field engineer sees the *open* tickets of the other engineers at their own partner company,
 * as a list and on a map, so they can spot a job near them and mail the service desk to take it
 * over. Everything here is read-only; no mutation in this file.
 *
 * Scoping is by partner company, resolved from `SmartTeamsFieldEngineer.partnerCompanyIds` rather
 * than from ServiceNow — ServiceNow has no notion of which NSC partner an engineer belongs to.
 */

/**
 * States that count as finished and are therefore excluded: 3 Closed Complete, 4 Closed
 * Incomplete, 7 Cancelled. Resolved (19) is deliberately kept — the FE work is done but the ticket
 * is still live, and a colleague may still need to see it.
 */
const CLOSED_STATE_VALUES = ['3', '4', '7'];

/**
 * How many `assigned_to` sys_ids go into one `assigned_toIN…` query.
 *
 * A partner company can have well over a hundred engineers (124 at NSC Dell on 2026-09-17) and each
 * sys_id is 32 characters, so a single IN clause would push the GET query string past 4 KB and
 * towards the server's URL limit. Chunking keeps every request comfortably small; because the view
 * is always scoped to a single day the per-chunk result stays far below `SN_PAGE_LIMIT`, so merging
 * the chunks loses nothing.
 */
const ASSIGNEE_CHUNK_SIZE = 50;

/** Hard ceiling the SmartTeams API puts on `limit` (spec §4.5). */
const SN_PAGE_LIMIT = 200;

/** `yyyy-mm-dd` → the SN datetime bounds of that day. Invalid input throws rather than silently widening. */
function dayBounds(date: string): { from: string; to: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw expectedError('Invalid date');
  return { from: `${date} 00:00:00`, to: `${date} 23:59:59` };
}

/**
 * Strip ServiceNow encoded-query operators from free text so a search term can never alter the
 * query's structure or widen the company scoping. Mirrors `sanitizeSearchTerm` in ticket-queries.ts.
 */
function sanitizeSearchTerm(raw: string | null | undefined): string {
  return (raw ?? '').replace(/[\^=,]/g, ' ').trim();
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * What the viewer may see, from their own engineer record (a field engineer is looked up by their login or
 * their ServiceNow id). Admins and partner managers need no record.
 */
async function resolveColleagueScope(appUser: Awaited<ReturnType<typeof getCurrentAppUser>>): Promise<ColleagueScope> {
  if (appUser.userGroup !== SmartTeamsAppUserGroup.FieldEngineer) {
    return colleagueScope({ userGroup: appUser.userGroup, feIsPartner: undefined, hasFeRecord: false });
  }
  const col = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
  const identity: Array<Record<string, string>> = [];
  if (appUser.userId) identity.push({ userId: appUser.userId });
  if (appUser.serviceNowFEId) identity.push({ serviceNowSysId: appUser.serviceNowFEId });
  const own =
    identity.length > 0
      ? await col.findOne({ deletedAt: null, $or: identity }, { projection: { isPartner: 1 } })
      : null;
  return colleagueScope({ userGroup: appUser.userGroup, feIsPartner: own?.isPartner, hasFeRecord: !!own });
}

/**
 * Whether the caller gets a Colleagues view at all — false for a partner engineer (they see only their own
 * tickets), so the app can hide the tab instead of showing an empty page.
 */
export const getMyColleagueAccess = registerQuery({
  operationName: 'getSmartTeamsAppMyColleagueAccess',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
    if (!allowed) return false;
    const appUser = await getCurrentAppUser();
    return (await resolveColleagueScope(appUser)) !== 'none';
  },
});

/**
 * Field engineers of the caller's own partner company that are linked to ServiceNow.
 *
 * Engineers without a `serviceNowSysId` are skipped: nothing in ServiceNow can be attributed to
 * them, so they would only ever be an empty option in the picker.
 */
async function loadCompanyColleagues(): Promise<ColleagueEngineer[]> {
  const appUser = await getCurrentAppUser();
  if (!appUser.companyId) return [];

  // Partner / internal rules (see _colleague-scope.ts): which kind of engineer this viewer may see.
  const scope = await resolveColleagueScope(appUser);
  if (scope === 'none') return [];

  const col = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
  const engineers = await col
    .find(
      {
        ...engineerTypeFilter(scope),
        partnerCompanyIds: appUser.companyId,
        deletedAt: null,
        archived: { $ne: true },
        serviceNowSysId: { $nin: [null, ''] },
      },
      { projection: { name: 1, email: 1, serviceNowSysId: 1 } },
    )
    .toArray();

  return engineers
    .map(
      fe =>
        new ColleagueEngineer({ sysId: fe.serviceNowSysId!, name: fe.name || fe.email || '', email: fe.email || '' }),
    )
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
}

/**
 * The FE picker's options: every engineer at the caller's partner company that is linked to
 * ServiceNow. The client prepends its own "All" entry.
 */
export const getColleagueEngineers = registerQuery({
  operationName: 'getSmartTeamsAppColleagueEngineers',
  type: () => [ColleagueEngineer],
  params: [],
  resolve: () => async (_gql: BaseQLSelectionSet<ColleagueEngineer>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
    if (!allowed) throw expectedError('Insufficient permissions to view tickets');
    return loadCompanyColleagues();
  },
});

/**
 * Open tickets of the caller's colleagues on one day.
 *
 * Filtered on `window_start`, not `expected_start`: `expected_start` is empty on every ticket the
 * live instance returns, while every assigned ticket carries a scheduling window (verified against
 * nsctest, 2026-09-17).
 *
 * `engineerSysId` narrows to a single colleague and is validated against the company roster, so a
 * caller can't read another company's tickets by passing an arbitrary sys_id. `search` matches the
 * ticket number.
 */
export const getColleagueTickets = registerQuery({
  operationName: 'getSmartTeamsAppColleagueTickets',
  type: () => [ServiceNowTicket],
  params: [
    { name: 'date', type: () => String },
    { name: 'engineerSysId', type: () => String },
    { name: 'search', type: () => String },
  ],
  resolve:
    (date: string, engineerSysId?: string, search?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
      if (!allowed) throw expectedError('Insufficient permissions to view tickets');

      const colleagues = await loadCompanyColleagues();
      if (colleagues.length === 0) return [];

      // Validate the requested engineer against the roster rather than trusting the client —
      // otherwise any sys_id would be readable through this query.
      let sysIds = colleagues.map(c => c.sysId!).filter(Boolean);
      if (engineerSysId) {
        if (!sysIds.includes(engineerSysId)) throw expectedError('Unknown field engineer');
        sysIds = [engineerSysId];
      }

      const { from, to } = dayBounds(date);
      const term = sanitizeSearchTerm(search);

      const { getTicketList } = await import('@nsc/servicenow/smartteam-api.js');

      const baseConds = [
        `window_start>=${from}`,
        `window_start<=${to}`,
        ...CLOSED_STATE_VALUES.map(state => `state!=${state}`),
        ...(term ? [`numberLIKE${term}`] : []),
      ];

      const batches = await Promise.all(
        chunk(sysIds, ASSIGNEE_CHUNK_SIZE).map(ids =>
          getTicketList({
            limit: SN_PAGE_LIMIT,
            offset: 0,
            sortBy: 'window_start',
            sortDirection: 'asc',
            query: [`assigned_toIN${ids.join(',')}`, ...baseConds].join('^'),
          }),
        ),
      );

      // Merge the chunks and de-duplicate by ticket number — a ticket can only have one assignee,
      // so an overlap would mean SN returned the same row twice, but be defensive.
      const byNumber = new Map<string, SNSmartTeamTicket>();
      // Fail closed: ServiceNow was asked only for the roster's engineers, but keep nothing whose assignee is not
      // on it — a partner's ticket must never reach an internal engineer (or the other way round).
      const rosterSysIds = new Set(sysIds);
      for (const sn of batches.flat()) {
        const number = sn.number?.display_value || sn.number?.value;
        const assignee = snAssignedTo(sn)?.value;
        if (number && assignee && rosterSysIds.has(assignee) && !byNumber.has(number)) byNumber.set(number, sn);
      }
      const snTickets = [...byNumber.values()].sort((a, b) =>
        (a.scheduling?.window_start?.value ?? '').localeCompare(b.scheduling?.window_start?.value ?? ''),
      );

      const tickets = await Promise.all(snTickets.map(sn => mapSNTicketForColleagueView(sn)));
      await attachWorkNotes(tickets);
      return tickets;
    },
});

/**
 * Every PUDO depot, geocoded for the map.
 *
 * ServiceNow returns no coordinates for depots, so each address is geocoded once and cached in
 * `smartteams_pudo_location_cache`; a depot whose address can't be resolved comes back with null
 * coordinates and is simply not placed on the map.
 */
export const getPudoLocations = registerQuery({
  operationName: 'getSmartTeamsAppPudoLocations',
  type: () => [PudoLocation],
  params: [],
  resolve: () => async (_gql: BaseQLSelectionSet<PudoLocation>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
    if (!allowed) throw expectedError('Insufficient permissions to view tickets');

    const { getPudoLocationList } = await import('@nsc/servicenow/smartteam-api.js');
    const depots = await getPudoLocationList();

    const cacheCol = await getRawCollection<PudoLocationCache>(PudoLocationCache._collectionName);
    const cached = await cacheCol.find({}).toArray();
    const cacheBySysId = new Map(cached.map(entry => [entry.sysId, entry] as const));

    const results: PudoLocation[] = [];
    for (const depot of depots) {
      const addressLabel = [depot.street, depot.zip_code, depot.city]
        .map(p => p?.trim())
        .filter(Boolean)
        .join(', ');

      let coords: { lat: number; lng: number } | null = null;
      const hit = cacheBySysId.get(depot.sysId);
      if (hit && hit.addressLabel === addressLabel && typeof hit.lat === 'number' && typeof hit.lng === 'number') {
        coords = { lat: hit.lat, lng: hit.lng };
      } else if (addressLabel) {
        coords = await geocode(addressLabel);
        if (coords) {
          await cacheCol.updateOne(
            { sysId: depot.sysId },
            { $set: { sysId: depot.sysId, addressLabel, lat: coords.lat, lng: coords.lng, geocodedAt: new Date() } },
            { upsert: true },
          );
        }
      }

      results.push(
        new PudoLocation({
          sysId: depot.sysId,
          name: depot.name,
          company: depot.company,
          street: depot.street,
          zipCode: depot.zip_code,
          city: depot.city,
          siteAccessHours: depot.site_access_hours,
          lat: coords?.lat,
          lng: coords?.lng,
        }),
      );
    }
    return results;
  },
});
